import React from "react";
import { useFormContext, Controller } from 'react-hook-form';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import calendar from '../../assets/svg/calendar.svg';

// Define the props interface
interface DateFieldProps {
    name: string;
    onChange?: any;
    placeholder?: string;
    disabled?: boolean;
    min?: Date;
    max?: Date;
    isClearable?: boolean;
    selectedDate?: Date | undefined | null;
    className?: string;
    label?: string;
    selectSingleMonth?: boolean; // New prop for single month selection
    control?: any;
}

const CustomDateInput = React.forwardRef<HTMLInputElement, any>(
    ({ value, onClick, placeholder, className, isClearable }: any, ref) => (
        <div
            className={`flex items-center justify-between w-full ${className} bg-white border border-feildBorder rounded-basicRadius shadow-shadowAll p-3 cursor-pointer mb-2`}
            onClick={onClick}
            ref={ref}
        >
            <span className="text-sm text-darkGrey">
                {value || placeholder}
            </span>
            <img src={calendar} alt="Calendar Icon" className="w-4 h-4 ml-2" style={!isClearable ? { visibility: 'visible' } : { visibility: 'hidden' }} />
        </div>
    )
);

CustomDateInput.displayName = "CustomDateInput";

export default function DateField({
    name,
    onChange,
    placeholder,
    disabled,
    min,
    max,
    isClearable = false,
    selectedDate,
    className,
    label,
    selectSingleMonth = false,
    control
}: DateFieldProps) {
    const formContext = useFormContext();
    const controlTemp = formContext?.control || control;

    const onChangeHandler = (date: Date | null) => {
        onChange(date, name);
    };

    if (!controlTemp) {
        // If not using react-hook-form, fallback to controlled textarea
        return (
            <div className="datepicker relative monthpicker">
                <React.Fragment>
                    {label ? (
                        <div className="text-sm capitalize font-aeonikReg mb-2 relative inline-block text-darkGrey min-h-[1.40625rem]">
                            {label}
                        </div>
                    ) : null}
                    <DatePicker
                        dateFormat={selectSingleMonth ? "MM/yyyy" : "dd/MM/yyyy"} // Format for month or day
                        placeholderText={placeholder}
                        disabled={disabled}
                        isClearable={isClearable}
                        selected={selectedDate}
                        onChange={onChangeHandler}
                        showMonthYearPicker={selectSingleMonth} // Enables month selection
                        showPopperArrow={false}
                        minDate={min}
                        maxDate={max}
                        customInput={<CustomDateInput placeholder={placeholder} className={className} isClearable={isClearable} />}
                    />
                </React.Fragment>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="datepicker relative ">
                <Controller
                    name={name || ""}
                    control={controlTemp}
                    render={({ field, fieldState: { error } }) => (
                        <React.Fragment>
                            {label ? (
                                <div className="text-sm capitalize font-aeonikReg mb-2 relative inline-block text-darkGrey min-h-[1.40625rem]">
                                    {label}
                                </div>
                            ) : null}
                            <DatePicker
                                {...field}
                                dateFormat={selectSingleMonth ? "MM/yyyy" : "dd/MM/yyyy"} // Format for month or day
                                placeholderText={placeholder}
                                disabled={disabled}
                                isClearable={isClearable}
                                selected={field.value}
                                showMonthYearPicker={selectSingleMonth} // Enables month selection
                                showPopperArrow={false}
                                minDate={min}
                                maxDate={max}
                                onKeyDown={(e) => e?.preventDefault()}
                                ref={(ref: any) => {
                                    field.ref({
                                        ...field,
                                        focus: false || (() => ref?.focus?.()), // Safely bind focus
                                    });
                                }}
                                customInput={<CustomDateInput placeholder={placeholder} className={className} isClearable={isClearable} />}
                            />
                            {error && <span className="text-sm text-rejectDark">{error.message}</span>}
                        </React.Fragment>
                    )}
                />
            </div>
        </React.Fragment>
    );
}